/**
 * 처방 검수 상세 ↔ 이전 처방 불러오기 새 창 연동.
 *
 * 두 창은 같은 출처라 BroadcastChannel로 메시지를 주고받는다. 채널은 처방 이미지 코드별로 나눈다.
 * - 상세 화면(host): 미저장·저장된 의약품 코드를 새 창에 알려 주고, 새 창이 고른 약을 미저장 행으로 받는다.
 * - 새 창(client): 열리면 상태를 요청하고, [추가] 시 고른 약과 처방량을 상세 화면으로 보낸다.
 * 상세 화면을 닫거나 다른 처방으로 옮기면 연동이 끊기고 새 창에 안내한다.
 */
import { prescriptionReviewPreviousDrugsPath } from '@/shared/constants/routes';
import { useEffect, useRef, useState } from 'react';
import type { PreviousDrugAddition } from './PreviousPrescriptionDrugsPanel';

export const previousDrugsChannelName = (prescriptionImageCode: string) =>
  `ruda-admin:previous-drugs:${prescriptionImageCode}`;

/** 새 창이 상태 요청 후 이 시간 안에 답이 없으면 상세 화면이 닫힌 것으로 본다 */
export const PREVIOUS_DRUGS_HOST_TIMEOUT_MS = 1500;

export type HostToWindowMessage =
  | {
      type: 'state';
      /** 상세 화면 미저장 행의 의약품 코드(trim) */
      stagedDrugCodes: string[];
      /** 상세 화면에 이미 저장된 의약품 코드(trim) — 새 창 조회 이후 저장된 약도 막기 위해 */
      registeredDrugCodes: string[];
    }
  | { type: 'host-closed' };

export type WindowToHostMessage =
  | { type: 'request-state' }
  | { type: 'add'; additions: PreviousDrugAddition[] };

/**
 * 상세 화면 쪽 — 새 창을 열고, 새 창이 보낸 약을 onAdd로 받는다.
 * 미저장·저장 코드가 바뀔 때마다 새 창에 다시 알린다.
 */
export const usePreviousDrugsWindowHost = ({
  prescriptionImageCode,
  stagedDrugCodes,
  registeredDrugCodes,
  onAdd,
}: {
  prescriptionImageCode: string;
  stagedDrugCodes: ReadonlySet<string>;
  registeredDrugCodes: ReadonlySet<string>;
  onAdd: (additions: PreviousDrugAddition[]) => void;
}) => {
  const channelRef = useRef<BroadcastChannel | null>(null);
  // 메시지 핸들러가 최신 상태·콜백을 보도록 ref로 들고 있는다
  const stateRef = useRef<HostToWindowMessage>({
    type: 'state',
    stagedDrugCodes: [],
    registeredDrugCodes: [],
  });
  const onAddRef = useRef(onAdd);
  onAddRef.current = onAdd;

  stateRef.current = {
    type: 'state',
    stagedDrugCodes: [...stagedDrugCodes],
    registeredDrugCodes: [...registeredDrugCodes],
  };

  useEffect(() => {
    if (!prescriptionImageCode || typeof BroadcastChannel === 'undefined') {
      return;
    }
    const channel = new BroadcastChannel(
      previousDrugsChannelName(prescriptionImageCode),
    );
    channelRef.current = channel;
    channel.onmessage = (event: MessageEvent<WindowToHostMessage>) => {
      const message = event.data;
      if (message?.type === 'request-state') {
        channel.postMessage(stateRef.current);
      } else if (message?.type === 'add') {
        onAddRef.current(message.additions);
      }
    };
    // 탭을 닫거나 새로고침할 때도 새 창이 알 수 있게 pagehide에서 알린다
    const notifyClosed = () =>
      channel.postMessage({
        type: 'host-closed',
      } satisfies HostToWindowMessage);
    window.addEventListener('pagehide', notifyClosed);
    // 새 창이 먼저 열려 있던 경우(상세 화면 새로고침 등)에도 바로 다시 붙도록 상태를 알린다
    channel.postMessage(stateRef.current);
    return () => {
      window.removeEventListener('pagehide', notifyClosed);
      notifyClosed();
      channel.close();
      channelRef.current = null;
    };
  }, [prescriptionImageCode]);

  // 미저장·저장 코드 목록이 바뀌면 새 창에 다시 알린다
  const stateKey = `${[...stagedDrugCodes].sort().join(',')}|${[
    ...registeredDrugCodes,
  ]
    .sort()
    .join(',')}`;
  useEffect(() => {
    channelRef.current?.postMessage(stateRef.current);
  }, [stateKey]);

  /** 새 창을 연다. 같은 처방은 같은 창 이름을 써서 이미 열려 있으면 그 창을 다시 쓴다 */
  const openWindow = (): boolean => {
    const width = Math.min(1400, window.screen.availWidth - 80);
    const height = Math.min(900, window.screen.availHeight - 80);
    const popup = window.open(
      prescriptionReviewPreviousDrugsPath(prescriptionImageCode),
      `previous-drugs-${prescriptionImageCode}`,
      `width=${width},height=${height},resizable=yes,scrollbars=yes`,
    );
    if (!popup) return false;
    popup.focus();
    return true;
  };

  return { openWindow };
};

export type PreviousDrugsHostStatus = 'connecting' | 'connected' | 'closed';

/**
 * 새 창 쪽 — 상세 화면의 미저장·저장 코드를 받아 두고, 고른 약을 상세 화면으로 보낸다.
 */
export const usePreviousDrugsWindowClient = (prescriptionImageCode: string) => {
  const channelRef = useRef<BroadcastChannel | null>(null);
  const [status, setStatus] = useState<PreviousDrugsHostStatus>('connecting');
  const [stagedDrugCodes, setStagedDrugCodes] = useState<ReadonlySet<string>>(
    () => new Set(),
  );
  const [registeredDrugCodes, setRegisteredDrugCodes] = useState<
    ReadonlySet<string>
  >(() => new Set());

  useEffect(() => {
    if (!prescriptionImageCode || typeof BroadcastChannel === 'undefined') {
      setStatus('closed');
      return;
    }
    const channel = new BroadcastChannel(
      previousDrugsChannelName(prescriptionImageCode),
    );
    channelRef.current = channel;
    const timeout = window.setTimeout(() => {
      setStatus((prev) => (prev === 'connecting' ? 'closed' : prev));
    }, PREVIOUS_DRUGS_HOST_TIMEOUT_MS);

    channel.onmessage = (event: MessageEvent<HostToWindowMessage>) => {
      const message = event.data;
      if (message?.type === 'state') {
        setStagedDrugCodes(new Set(message.stagedDrugCodes));
        setRegisteredDrugCodes(new Set(message.registeredDrugCodes));
        setStatus('connected');
      } else if (message?.type === 'host-closed') {
        setStatus('closed');
      }
    };
    channel.postMessage({
      type: 'request-state',
    } satisfies WindowToHostMessage);

    return () => {
      window.clearTimeout(timeout);
      channel.close();
      channelRef.current = null;
    };
  }, [prescriptionImageCode]);

  /** 고른 약을 상세 화면으로 보낸다. 연결이 끊겼으면 false */
  const sendAdditions = (additions: PreviousDrugAddition[]): boolean => {
    if (status !== 'connected' || !channelRef.current) return false;
    channelRef.current.postMessage({
      type: 'add',
      additions,
    } satisfies WindowToHostMessage);
    return true;
  };

  return { status, stagedDrugCodes, registeredDrugCodes, sendAdditions };
};
